import React, { useEffect, useState, useCallback } from 'react';

import { hasRoles, EDIT_CLIENT_ROLES } from '../../../../util/RoleUtil';

import { viewClient } from '../../../../services/ClientService';

import { Table } from '../../../common/table/Table';
import Modal from '../../../common/modal/Modal';
import { LinkClientToStudy } from '../../../studies-and-trials/participants/link-new/LinkClientToStudy';

import './ClientsViewStudy.css';

export const ClientsViewStudy = ({
  context,
  setContext,
  client,
  setClient,
  navigateTo,
  showToaster,
  setSpinnerText,
  navigateToWith,
  toolTipsEnabled,
}) => {
  const [searchResults, setSearchResults] = useState([]);
  const [isLinkingModalOpen, setIsLinkingModalOpen] = useState(false);

  const refreshClient = useCallback(async () => {
    setSpinnerText('Refreshing Studies');

    const [data, error] = await viewClient(context.username, client.externalReference);

    if (data?.responseStatus === 'Success') {
      setClient(data.clientVO);
    } else if (error) {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }

    setSpinnerText(null);
  }, [context.username, client.externalReference, setClient, setSpinnerText, showToaster]);

  useEffect(() => {
    if (client && client.studies) {
      setSearchResults(
        client.studies.map((study) => ({
          studyExternalReference: study.studyExternalReference,
          id: study.idInStudy,
          idType: study.idType,
          participantStatus: study.participantStatus,
          participantExternalReference: study.participantExternalReference,
          protocolID: study.protocolID,
          dateEnded: study.dateEnded,
          coEnrollmentAllowed: study.coEnrollmentAllowed,
        }))
      );
    }
  }, [setSearchResults, client]);

  useEffect(() => {
    if (context.currentScreen === 'clientsView' && client?.externalReference) {
      refreshClient();
    }
  }, [context.currentScreen, client?.externalReference, refreshClient]);

  const handleRowClick = (participant) => {
    navigateToWith('participantEdit', 'participantClientDetails', {
      participantExternalReference: participant.participantExternalReference,
    });
  };

  const handleLinkToStudyButtonClick = () => {
    setContext((prev) => ({
      ...prev,
      navigateContext: undefined,
    }));

    setIsLinkingModalOpen(true);
  };

  const closeLinkingModal = async () => {
    setIsLinkingModalOpen(false);
    const [data, error] = await viewClient(context.username, client.externalReference);
    if (data) {
      if (data.responseStatus === 'Success') {
        setClient(data.clientVO);
        setContext((prevState) => ({
          ...prevState,
          activeTab: '',
          viewMode: false,
          breadcrumbs: {
            ...prevState.breadcrumbs,
            viewClientBreadcrumb: {
              ...prevState.breadcrumbs.viewClientBreadcrumb,
              type: 'General',
              screenKey: 'clientsView',
              data: data,
            },
          },
          editMode: true,
        }));
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Client Information Retrieved',
        });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  return (
    <div className={'client-view-study-container'}>
      {searchResults && (
        <Table
          tableData={searchResults}
          hiddenColumns={[
            'studyExternalReference',
            'externalReference',
            'coEnrollmentAllowed',
            'participantExternalReference',
          ]}
          searchEnabled={true}
          filterEnabled={true}
          onRowClick={handleRowClick}
          tableTitle={'Previous & Ongoing Studies'}
          buttonText={hasRoles(context, EDIT_CLIENT_ROLES) ? 'Link to Study' : undefined}
          onButtonClick={handleLinkToStudyButtonClick}
          buttonDisabled={false}
          maxRowCount={8}
          horizontalScroll
        />
      )}

      {isLinkingModalOpen && (
        <Modal
          closeModal={closeLinkingModal}
          setIsModalOpen={setIsLinkingModalOpen}
          hideButton={true}
          headerTitle={'Link Client to Study'}
          width={'650px'}
          justifyContent={'center'}
          contentPadding={'2px'}
        >
          <LinkClientToStudy
            client={client}
            closeModal={closeLinkingModal}
            setSpinnerText={setSpinnerText}
            showToaster={showToaster}
            context={context}
            navigateTo={navigateTo}
            navigateToWith={navigateToWith}
            toolTipsEnabled={toolTipsEnabled}
          />
        </Modal>
      )}
    </div>
  );
};
